import React from "react";
import { fetchQuery } from "react-relay";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";

import type { CreateOrImportProps } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Input } from "~/ui/legacyFellowIndex";
import toasty from "~/ui/toast";

import type { ImportExistingTicketPaneMutation } from "~/__generated__/ImportExistingTicketPaneMutation.graphql";
import type { ImportExistingTicketPaneTicketQuery } from "~/__generated__/ImportExistingTicketPaneTicketQuery.graphql";

import styles from "~/integrations/clickup/css/styles.module.css";

const fetchTaskQuery = graphql`
	query ImportExistingTicketPaneTicketQuery($taskId: String!, $workspaceId: String) {
		viewer {
			integrations {
				clickup {
					task(taskId: $taskId, workspaceId: $workspaceId) {
						id
						name
						externalId
					}
				}
			}
		}
	}
`;

type TaskType = NonNullable<
	ImportExistingTicketPaneTicketQuery["response"]["viewer"]["integrations"]["clickup"]
>["task"];

function getTaskById(taskId: string, workspaceId?: string) {
	return fetchQuery<ImportExistingTicketPaneTicketQuery>(environment, fetchTaskQuery, {
		taskId,
		workspaceId,
	}).toPromise();
}

const importExternalTicket = graphql`
	mutation ImportExistingTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$initialText: String!
		$destinationInfo: JSON!
	) {
		loadActionItemFromIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				initialText: $initialText
				destinationInfo: $destinationInfo
				integrationProvider: CLICKUP
			}
		) {
			clientMutationId
		}
	}
`;

function importActionItem(blockKey: string, noteId: string, taskId: string, initialText: string, workspaceId?: string) {
	const destinationInfo = workspaceId ? { workspaceId: workspaceId, taskId: taskId } : { taskId: taskId };

	return commitMutationPromise<ImportExistingTicketPaneMutation>(environment, {
		mutation: importExternalTicket,
		variables: { blockKey, noteId, destinationInfo: destinationInfo, initialText },
	});
}

function stripHashFromTaskId(taskId: string) {
	return taskId.replace(/^#/, "");
}

export function ImportExistingTicketPane({
	setCanSubmit,
	registerSubmit,
	blockKey,
	noteId,
	initialText,
}: CreateOrImportProps) {
	const [taskId, setTaskId] = React.useState<string>("");
	const debouncedTaskId = useDebouncedValue(taskId, 300);
	const [task, setTask] = React.useState<TaskType>();
	const [lastErroredId, setLastErroredId] = React.useState<string>();
	const [loading, setLoading] = React.useState(false);
	const [workspaceId, setWorkspaceId] = React.useState<string>();

	React.useEffect(() => {
		if (debouncedTaskId === "") {
			return;
		}

		setLoading(true);

		getTaskById(debouncedTaskId, workspaceId).then(result => {
			if (result) {
				const {
					viewer: {
						integrations: { clickup },
					},
				} = result;

				if (clickup) {
					const { task } = clickup;

					if (task) {
						setTask(task);
						setLoading(false);
						return;
					}
				}
			}
			setLastErroredId(debouncedTaskId);
			setLoading(false);
		});
	}, [debouncedTaskId, workspaceId]);

	const parseTaskIdAndSpace = (input: string) => {
		/*
			Acceptable inputs:

			Task URL with custom ID: https://app.clickup.com/t/36753137/TEST-1
			Task URL with default ID: https://app.clickup.com/t/2chwf1b
			Default ID: 2chwf1b
		*/

		const spaceAndIdRegex = /https?:\/\/app\.clickup\.com\/t\/(\d+)\/(\w+-\d+)|(?!clickup)(\w{7,})/gi;
		const matches = spaceAndIdRegex.exec(input)?.filter(Boolean);

		if (matches) {
			if (matches.length == 2) {
				// Regular task URL
				setTaskId(matches[1]);
			} else if (matches.length == 3) {
				// Task URL with workspace
				setWorkspaceId(matches[1]);
				setTaskId(matches[2]);
			}
		}
	};

	const onTitleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target.value;

		if (input.includes("http")) {
			parseTaskIdAndSpace(e.target.value);
		} else {
			const taskId = stripHashFromTaskId(input);
			if (taskId.includes("-")) {
				setLastErroredId(taskId);
				return;
			}
			setTaskId(stripHashFromTaskId(input));
		}
	}, []);

	const submit = React.useCallback(async () => {
		if (!task) {
			throw new Error("Invalid state, submit should not be available if task is null");
		}

		try {
			await importActionItem(blockKey, noteId, task.externalId, initialText, workspaceId);
		} catch (e) {
			if (Array.isArray(e)) {
				for (let exception of e) {
					if ("message" in exception) toasty.error(exception.message);
				}
			}
		}
		return true;
	}, [blockKey, noteId, task, initialText, workspaceId]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		// check state is valid to be submitted on state change.
		setCanSubmit(!!task && taskId === task.externalId);
	}, [setCanSubmit, task, taskId]);

	const errored = lastErroredId === taskId;

	return (
		<div>
			<label className={styles.title}>
				<div className={styles.label}>
					Clickup Task ID or URL{" "}
					{loading && <FontAwesomeIcon icon={faSpinnerThird} spin color="var(--primaryButtonColor)" />}
				</div>
				<Input
					name="clickup_task_id"
					autoFocus
					className={cx(styles.titleInput, { [styles.titleInputErrored]: errored })}
					type="text"
					placeholder="ClickUp Task ID"
					onChange={onTitleChange}
				/>
				<p className={cx(styles.labelHelpText, { [styles.labelHelpTextErrored]: errored })}>
					You can copy the task ID from the task menu in ClickUp or copy the URL from the browser. If your Space uses
					custom IDs (e.g. ABC-123), the entire task URL must be pasted here.
				</p>
			</label>
		</div>
	);
}
